import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { canAccessPoolWorkflow, formatWorkflowError } from '../utils/workflowGuards';
import API from '../api/axios';
import { Icon } from '../components/SvgIcons';
import './UniversityPoolExaminer.css';

const UniversityPoolExaminer = () => {
  const { user } = useContext(AuthContext);
  const { showToast } = useToast();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('pools');
  const [availablePools, setAvailablePools] = useState([]);
  const [selectedPool, setSelectedPool] = useState(null);
  const [poolStructure, setPoolStructure] = useState(null);
  const [myContributions, setMyContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingVariations, setGeneratingVariations] = useState(false);
  const [selectedSubQuestion, setSelectedSubQuestion] = useState(null);
  const [variationForm, setVariationForm] = useState({
    num_variations: 20,
    subject: '',
    topic: '',
    chapters: '',
    language: 'english',
    fileId: null,
    usePdfContext: false
  });
  const [myVariations, setMyVariations] = useState([]);
  const [showVariationModal, setShowVariationModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedVariations, setSelectedVariations] = useState([]);
  const [certificationStatus, setCertificationStatus] = useState(null);

  const checkCertification = useCallback(async () => {
    try {
      const { data } = await API.get('/faculty-certification/my-certification');
      if (data.success) {
        setCertificationStatus(data.certification);
        if (!data.certification.is_certified) {
          showToast('University certification required to access this feature', 'error');
        }
      }
    } catch (err) {
      showToast('Failed to verify certification status', 'error');
      setLoading(false);
    }
  }, [showToast]);

  const fetchAvailablePools = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/university-pool-examiner/available-pools');
      
      if (data.success) {
        setAvailablePools(data.pool_papers || []);
      }
    } catch (err) {
      const errorMessage = formatWorkflowError(err.response?.data || err, 'pool');
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    // Check if user can access pool workflow
    if (!canAccessPoolWorkflow(user)) {
      showToast('University certification required to access this feature', 'error');
      navigate('/examiner');
      return;
    }
    checkCertification();
  }, [user, navigate, showToast, checkCertification]);

  useEffect(() => {
    if (certificationStatus?.is_certified) {
      fetchAvailablePools();
    }
  }, [certificationStatus, fetchAvailablePools]);



  const fetchPoolStructure = async (poolId) => {
    try {
      setLoading(true);
      const { data } = await API.get(`/university-pool-examiner/pools/${poolId}/structure`);
      
      if (data.success) {
        setPoolStructure(data);
        setSelectedPool(poolId);
        setActiveTab('contribute');
        fetchMyContributions(poolId);
      }
    } catch (err) {
      const errorMessage = formatWorkflowError(err.response?.data || err, 'pool');
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyContributions = async (poolId) => {
    try {
      const { data } = await API.get(`/university-pool-examiner/pools/${poolId}/my-contributions`);
      
      if (data.success) {
        setMyContributions(data.contributions || []);
      }
    } catch (err) {
      console.error('Failed to fetch contributions:', err);
    }
  };

  const fetchMyVariations = async (poolId, subQuestionId) => {
    try {
      const { data } = await API.get(`/university-pool-examiner/pools/${poolId}/sub-questions/${subQuestionId}/my-variations`);
      
      if (data.success) {
        setMyVariations(data.variations || []);
      }
    } catch (err) {
      showToast('Failed to fetch variations', 'error');
    }
  };

  const handleGenerateVariations = async () => {
    if (!selectedSubQuestion || variationForm.num_variations < 4) {
      showToast('Please select a sub-question and enter at least 4 variations', 'error');
      return;
    }

    if (variationForm.num_variations > 40) {
      showToast('Maximum 40 variations allowed for university examiners', 'error');
      return;
    }

    try {
      setGeneratingVariations(true);
      
      // Use the existing sub-questions generation endpoint with pool context
      const { data } = await API.post('/sub-questions/generate-variations', {
        sub_question_id: selectedSubQuestion.pool_sub_question_id,
        ...variationForm,
        is_university_pool: true,
        pool_paper_id: selectedPool
      });

      if (data.success) {
        showToast(`Started generating ${variationForm.num_variations} variations`, 'success');
        setShowVariationModal(false);
        // Refresh variations after generation
        setTimeout(() => {
          fetchMyVariations(selectedPool, selectedSubQuestion.pool_sub_question_id);
        }, 2000);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to generate variations', 'error');
    } finally {
      setGeneratingVariations(false);
    }
  };

  const handleSendToPool = async () => {
    if (selectedVariations.length < 4 || selectedVariations.length > 10) {
      showToast('Please select 4-10 variations to send to pool', 'error');
      return;
    }

    try {
      const { data } = await API.post(
        `/university-pool-examiner/pools/${selectedPool}/sub-questions/${selectedSubQuestion.pool_sub_question_id}/send-to-pool`,
        { variation_ids: selectedVariations }
      );

      if (data.success) {
        showToast(`${selectedVariations.length} variations sent to pool successfully`, 'success');
        setShowSendModal(false);
        setSelectedVariations([]);
        fetchMyVariations(selectedPool, selectedSubQuestion.pool_sub_question_id);
        fetchMyContributions(selectedPool);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to send variations', 'error');
    }
  };

  const handleConfirmAllContributions = async () => {
    if (!window.confirm('Are you sure you want to confirm all contributions? This action cannot be undone.')) {
      return;
    }

    try {
      const { data } = await API.post(`/university-pool-examiner/pools/${selectedPool}/confirm-contributions`);

      if (data.success) {
        showToast('All contributions confirmed successfully', 'success');
        fetchMyContributions(selectedPool);
        fetchPoolStructure(selectedPool);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to confirm contributions', 'error');
    }
  };

  const openVariationModal = (subQuestion) => {
    setSelectedSubQuestion(subQuestion);
    setVariationForm({
      ...variationForm,
      subject: poolStructure?.pool_paper?.subject_name || '',
      topic: poolStructure?.pool_paper?.paper_title || ''
    });
    setShowVariationModal(true);
  };

  const openSendModal = (subQuestion) => {
    setSelectedSubQuestion(subQuestion);
    fetchMyVariations(selectedPool, subQuestion.pool_sub_question_id);
    setShowSendModal(true);
  };

  const getContributionStatus = (subQuestion) => {
    const contribution = myContributions.find(c => 
      c.pool_sub_question_id === subQuestion.pool_sub_question_id
    );
    return contribution?.status || 'not_started';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <span className="status-badge confirmed">Confirmed</span>;
      case 'submitted':
        return <span className="status-badge submitted">Submitted</span>;
      case 'not_started':
        return <span className="status-badge not-started">Not Started</span>;
      default:
        return <span className="status-badge draft">Draft</span>;
    }
  };

  // Show certification requirement if not certified
  if (!certificationStatus?.is_certified) {
    return (
      <div className="university-pool-examiner-container">
        <div className="page-header">
          <h1>University Question Pool - Examiner</h1>
          <p>University certification required to access this feature</p>
        </div>
        
        <div className="certification-required">
          <div className="certification-card">
            <div className="certification-icon"><Icon name="GraduationCap" size={48} /></div>
            <h2>University Certification Required</h2>
            <p>
              To access the University Question Pool system, you need to be certified by your University Admin.
              This certification allows you to contribute to university-wide question pools across all colleges.
            </p>
            <div className="certification-benefits">
              <h3>Benefits of University Certification:</h3>
              <ul>
                <li>Contribute to university-wide question pools</li>
                <li>Collaborate with faculty from other colleges</li>
                <li>Access cross-college examination papers</li>
                <li>Enhanced professional recognition</li>
              </ul>
            </div>
            <p className="certification-contact">
              Please contact your University Admin to request certification.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="university-pool-examiner-container">
      <div className="page-header">
        <h1>University Question Pool - Examiner</h1>
        <p>Contribute variations to university-wide question pools</p>
        {user.certification_level === 'university' && (
          <div className="certification-badge">
            <span className="badge university-certified">University Certified</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs-header">
          <button 
            className={`tab-button ${activeTab === 'pools' ? 'active' : ''}`}
            onClick={() => setActiveTab('pools')}
          >
            Available Pools ({availablePools.length})
          </button>
          {selectedPool && (
            <button 
              className={`tab-button ${activeTab === 'contribute' ? 'active' : ''}`}
              onClick={() => setActiveTab('contribute')}
            >
              Contribute Variations
            </button>
          )}
        </div>

        <div className="tab-content">
          {activeTab === 'pools' && (
            <div className="pools-section">
              <div className="section-header">
                <h2>Available Question Pools</h2>
                <p>Select a pool to contribute variations</p>
              </div>

              {availablePools.length === 0 ? (
                <div className="empty-state">
                  <p>No available pools found for your subject</p>
                </div>
              ) : (
                <div className="pools-grid">
                  {availablePools.map(pool => (
                    <div key={pool.pool_paper_id} className="pool-card">
                      <div className="pool-header">
                        <h3>{pool.subject_name} - {pool.paper_title || 'General'}</h3>
                        <span className={`status-badge ${pool.status}`}>
                          {pool.status}
                        </span>
                      </div>
                      
                      <div className="pool-details">
                        <p><strong>Questions:</strong> {pool.question_count}</p>
                        <p><strong>My Contributions:</strong> {pool.my_contributions}</p>
                        <p><strong>Language:</strong> {pool.language}</p>
                        <p><strong>Total Marks:</strong> {pool.total_marks}</p>
                      </div>

                      <div className="pool-actions">
                        <button 
                          className="btn btn-primary"
                          onClick={() => fetchPoolStructure(pool.pool_paper_id)}
                        >
                          View & Contribute
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'contribute' && poolStructure && (
            <div className="contribute-section">
              <div className="section-header">
                <h2>{poolStructure.pool_paper.subject_name} - Contribution</h2>
                <p>Generate and send variations for each sub-question</p>
                <div className="contribution-actions">
                  <button 
                    className="btn btn-success"
                    onClick={handleConfirmAllContributions}
                    disabled={myContributions.some(c => c.status !== 'submitted')}
                  >
                    Confirm All Contributions
                  </button>
                </div>
              </div>

              <div className="questions-list">
                {poolStructure.questions.map(question => (
                  <div key={question.pool_question_id} className="question-section">
                    <h3>Question {question.question_number}</h3>

                    {/* Render sub-questions directly without hierarchy building */}
                    <div className="sub-questions-grid">
                      {question.sub_questions && question.sub_questions.length > 0 ? (
                        question.sub_questions.map(subQuestion => {
                          // Generate stable key for each sub-question
                          const subQuestionKey = subQuestion.pool_sub_question_id 
                            || `synthetic-${subQuestion.full_question_number}`;
                          
                          return (
                            <div key={subQuestionKey} className="sub-question-card">
                              <div className="sub-question-header">
                                <h4>{subQuestion.full_question_number}</h4>
                                {subQuestion.is_orphaned && (
                                  <span className="status-badge warning" title="These variations are not properly linked">Orphaned</span>
                                )}
                                {getStatusBadge(getContributionStatus(subQuestion))}
                              </div>

                              <div className="sub-question-details">
                                <p><strong>Type:</strong> {subQuestion.question_type}</p>
                                <p><strong>Marks:</strong> {subQuestion.marks}</p>
                                <p><strong>My Variations:</strong> {subQuestion.my_variations}</p>
                                <p><strong>Total Variations:</strong> {subQuestion.total_variations}</p>
                                {subQuestion.note && <p style={{color: 'var(--warning)', fontSize: '0.85rem'}}><em>{subQuestion.note}</em></p>}
                              </div>

                              <div className="sub-question-actions">
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => openVariationModal(subQuestion)}
                                  disabled={getContributionStatus(subQuestion) === 'confirmed'}
                                >
                                  Generate Variations
                                </button>
                                {subQuestion.my_variations > 0 && (
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => openSendModal(subQuestion)}
                                    disabled={getContributionStatus(subQuestion) === 'confirmed'}
                                  >
                                    Send to Pool ({subQuestion.my_variations})
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem' }}>
                          No sub-questions yet. Create sub-questions or generate variations for the parent question.
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Generate Variations Modal */}
      {showVariationModal && selectedSubQuestion && (
        <div className="modal-overlay" onClick={() => setShowVariationModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Generate Variations - {selectedSubQuestion.full_question_number}</h3>
              <button 
                className="modal-close"
                onClick={() => setShowVariationModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Number of Variations *</label>
                  <input
                    type="number"
                    className="form-input"
                    value={variationForm.num_variations}
                    onChange={(e) => setVariationForm({...variationForm, num_variations: parseInt(e.target.value)})}
                    min="4"
                    max="40"
                    required
                  />
                  <small className="form-help">University examiners: 4-40 variations</small>
                </div>

                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <input
                    type="text"
                    className="form-input"
                    value={variationForm.subject}
                    onChange={(e) => setVariationForm({...variationForm, subject: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Topic</label>
                  <input
                    type="text"
                    className="form-input"
                    value={variationForm.topic}
                    onChange={(e) => setVariationForm({...variationForm, topic: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Chapters</label>
                  <input
                    type="text"
                    className="form-input"
                    value={variationForm.chapters}
                    onChange={(e) => setVariationForm({...variationForm, chapters: e.target.value})}
                    placeholder="e.g., Chapter 5: Laws of Motion"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Language</label>
                  <select
                    className="form-select"
                    value={variationForm.language}
                    onChange={(e) => setVariationForm({...variationForm, language: e.target.value})}
                  >
                    <option value="english">English</option>
                    <option value="hindi">Hindi</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={variationForm.usePdfContext}
                    onChange={(e) => setVariationForm({...variationForm, usePdfContext: e.target.checked})}
                  />
                  Use PDF Context (if available)
                </label>
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowVariationModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleGenerateVariations}
                disabled={generatingVariations || variationForm.num_variations < 4}
              >
                {generatingVariations ? 'Generating...' : 'Generate Variations'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send to Pool Modal */}
      {showSendModal && selectedSubQuestion && (
        <div className="modal-overlay" onClick={() => setShowSendModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Send Variations to Pool - {selectedSubQuestion.full_question_number}</h3>
              <button 
                className="modal-close"
                onClick={() => setShowSendModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="selection-info">
                <p>Select 4-10 variations to send to the university pool for SME review.</p>
                <p><strong>Selected:</strong> {selectedVariations.length} / 10</p>
              </div>

              <div className="variations-list">
                {myVariations.map(variation => (
                  <div key={variation.variation_id} className="variation-item">
                    <div className="variation-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedVariations.includes(variation.variation_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            if (selectedVariations.length < 10) {
                              setSelectedVariations([...selectedVariations, variation.variation_id]);
                            }
                          } else {
                            setSelectedVariations(selectedVariations.filter(id => id !== variation.variation_id));
                          }
                        }}
                        disabled={!selectedVariations.includes(variation.variation_id) && selectedVariations.length >= 10}
                      />
                    </div>
                    <div className="variation-content">
                      <div className="variation-header">
                        <span className="variation-number">#{variation.variation_number}</span>
                        <span className="quality-score">Quality: {variation.quality_score}</span>
                      </div>
                      <div className="variation-text">
                        {variation.question_text}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowSendModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSendToPool}
                disabled={selectedVariations.length < 4 || selectedVariations.length > 10}
              >
                Send {selectedVariations.length} Variations to Pool
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversityPoolExaminer;